/**
 * Shared user-facing copy for API failures.
 *
 * Callers used to inline `err.response?.data?.error || 'Failed'`, which shows
 * the literal word "Failed" when the request never reached the server (offline,
 * DNS, proxy down) because there is no `response` to read at all.
 */

const OFFLINE = 'You appear to be offline. Check your connection and try again — nothing was saved.';
const UNREACHABLE = 'Could not reach the server. It may be restarting, or the API base URL may be misconfigured.';
const TIMEOUT = 'The server took too long to respond. If you were uploading a file, check your connection and try again.';

function serverMessage(data) {
  if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data.trim();
  if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  const list = validationMessages(data);
  return list.length ? list.join(' ') : null;
}

function validationMessages(data) {
  if (!Array.isArray(data?.errors)) return [];
  return data.errors
    .map(e => (typeof e === 'string' ? e : e?.msg || e?.message))
    .filter(Boolean);
}

/** True when the request never got a response (offline, DNS, server down). */
export function isNetworkError(err) {
  return !!err && !err.response;
}

/** True when the browser reports no connectivity. */
export function isOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** True for a lost/invalid session. 403 is a permission problem, not a session problem. */
export function isSessionError(err) {
  return err?.response?.status === 401;
}

/**
 * Human copy for any axios error.
 * @param {Error} err axios error
 * @param {string} [fallback] copy for unclassified failures
 */
export function formatApiError(err, fallback = 'Something went wrong. Please try again.') {
  if (!err) return fallback;

  if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT' || /timeout/i.test(err.message || '')) {
    return TIMEOUT;
  }
  if (isNetworkError(err)) {
    return isOffline() ? OFFLINE : UNREACHABLE;
  }

  const { status, data } = err.response;
  const fromServer = serverMessage(data);

  if (status === 400 || status === 422) {
    return fromServer || 'Some of the details are invalid. Please review the form and try again.';
  }
  if (status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  if (status === 403) {
    return fromServer || "You don't have permission to do that.";
  }
  if (status === 404) {
    return fromServer || 'We could not find what you were looking for. It may have been removed.';
  }
  if (status === 409) {
    return fromServer || 'That conflicts with an existing record.';
  }
  if (status === 413) {
    return fromServer || 'That file is too large. Please upload a smaller file.';
  }
  if (status === 429) {
    return fromServer || 'Too many attempts. Please wait a minute and try again.';
  }
  if (status >= 500) {
    return 'The server ran into a problem. Please try again in a moment — if it keeps happening, contact your administrator.';
  }
  return fromServer || fallback;
}

/**
 * Copy for one-time-token flows (password reset, admin invite, candidate
 * signup verification) where 400/404/410 all mean "this link is no longer good".
 * @param {Error} err axios error
 * @param {'reset'|'invite'|'verification'} [kind]
 */
export function formatTokenError(err, kind = 'reset') {
  const label = kind === 'invite' ? 'invitation' : kind === 'verification' ? 'verification' : 'reset';
  const reissue = kind === 'invite'
    ? 'Ask an administrator to send a new invitation.'
    : kind === 'verification'
      ? 'Request a new verification email from the sign-up page.'
      : 'Request a new password reset link.';

  if (!err || isNetworkError(err)) return formatApiError(err);

  const { status, data } = err.response;
  const fromServer = serverMessage(data);

  if (status === 400 || status === 401 || status === 404 || status === 410) {
    const looksLikeValidation = Array.isArray(data?.errors) && data.errors.length;
    if (looksLikeValidation) return fromServer;
    return fromServer || `This ${label} link is invalid, already used, or has expired. ${reissue}`;
  }
  return formatApiError(err, `Could not complete the ${label}. Please try again.`);
}

/**
 * Backend validation failures come back as `{ errors: [{ path, msg }] }` while
 * most callers only read `data.error`. Maps them to react-hook-form's
 * `setError(field, { message })` shape.
 * @returns {Object<string,string>} field -> message (empty object when none)
 */
export function mapValidationErrors(err) {
  const rows = err?.response?.data?.errors;
  if (!Array.isArray(rows)) return {};
  const out = {};
  for (const row of rows) {
    if (!row || typeof row === 'string') continue;
    const field = row.path || row.param || row.field;
    const message = row.msg || row.message;
    if (field && message && !out[field]) out[field] = message;
  }
  return out;
}

/** Validation messages as a flat list, for form-level summaries. */
export function validationErrorList(err) {
  return validationMessages(err?.response?.data);
}

/**
 * Same data as mapValidationErrors, as an ordered list that keeps unnamed
 * (form-level) messages. express-validator v7 uses `path`, v6 uses `param`.
 * @returns {{field: string|null, message: string}[]}
 */
export function serverFieldErrors(err) {
  const rows = err?.response?.data?.errors;
  if (!Array.isArray(rows)) return [];
  return rows
    .map(row => {
      if (typeof row === 'string') return { field: null, message: row };
      return {
        field: row?.path || row?.param || row?.field || null,
        message: row?.msg || row?.message || null,
      };
    })
    .filter(row => row.message);
}

/**
 * Marks react-hook-form fields from a validation response so the user sees
 * which field is wrong instead of a generic toast.
 * @param {Error} err axios error
 * @param {(field: string, error: {type: string, message: string}) => void} setError
 * @param {{fields?: string[]}} [options] restrict to fields the form owns
 * @returns {string[]} field names that were marked, in server order
 */
export function applyServerFieldErrors(err, setError, { fields } = {}) {
  const applied = [];
  for (const { field, message } of serverFieldErrors(err)) {
    if (!field) continue;
    if (fields && !fields.includes(field)) continue;
    setError(field, { type: 'server', message });
    applied.push(field);
  }
  return applied;
}
